import type { IncomingTaxRate } from '../incoming/types';

/**
 * Liest eine EINGEHENDE E-Rechnung und zieht die Erfassungsfelder heraus. Die Empfangspflicht gilt
 * seit 01.01.2025 -- bisher wurde jede Eingangsrechnung von Hand abgetippt. Unterstützt werden die
 * beiden in Deutschland vorgeschriebenen Formate:
 *   - XRechnung (UBL 2.1 Invoice / CreditNote) -- als eigenständige .xml
 *   - ZUGFeRD / Factur-X (UN/CEFACT CII, CrossIndustryInvoice) -- als eigenständige .xml
 *
 * Bewusst NICHT enthalten: das Herauslösen des eingebetteten XML aus einer ZUGFeRD-PDF/A-3 (braucht
 * einen PDF-Parser) -- das ist ein Folgeschritt. Der Verkäufer der E-Rechnung ist auf unserer Seite
 * der LIEFERANT; die interne Lieferantennummer steht nicht im XML und wird bei der Erfassung ergänzt.
 *
 * Namensraum-tolerant über `localName`, weil Absender die Präfixe (ubl:/cac:/ram:) frei wählen.
 */

export interface ParsedEInvoice {
  readonly format: 'ubl' | 'cii';
  readonly supplierName: string;
  readonly supplierVatId: string;
  readonly supplierInvoiceNumber: string;
  /** ISO YYYY-MM-DD, leer wenn nicht gefunden. */
  readonly invoiceDate: string;
  readonly netAmount: number;
  readonly taxRate: IncomingTaxRate;
  /**
   * Was beim Lesen NICHT eindeutig war und der Erfasser prüfen muss -- z. B. ein Steuersatz, den
   * FORQ nicht führt, oder eine Rechnung mit mehreren Sätzen. Leer = alles eindeutig übernommen.
   */
  readonly warnings: readonly string[];
}

export type EInvoiceParseResult =
  | { readonly ok: true; readonly data: ParsedEInvoice }
  | { readonly ok: false; readonly error: string };

const ALLOWED_RATES: readonly IncomingTaxRate[] = [0, 7, 19];

function isAllowedRate(percent: number): percent is IncomingTaxRate {
  return ALLOWED_RATES.some((rate) => rate === percent);
}

function formatPercent(percent: number): string {
  return `${percent.toLocaleString('de-DE', { maximumFractionDigits: 2 })} %`;
}

/** Ein gelesener Steuersatz mit seiner Bemessungsgrundlage -- zum Auswählen des maßgeblichen Satzes. */
interface GelesenerSteuersatz {
  readonly percent: number;
  readonly base: number;
}

/**
 * Den maßgeblichen Steuersatz bestimmen -- und JEDE Unschärfe benennen, statt sie wegzurunden.
 *
 * BEFUND (09/2026): Hier stand ein `nearestRate()`, das jeden unbekannten Satz still auf den
 * nächstgelegenen von 0/7/19 zog. Eine Lieferantenrechnung mit 16 % (Corona-Senkung, Altbestand)
 * oder einem ausländischen Satz wurde damit kommentarlos zu 19 % -- die Vorsteuer stimmte danach
 * nicht, und niemand konnte es sehen. Zudem wurde nur der ERSTE Steuersatz gelesen, sodass eine
 * Rechnung mit 19 % und 7 % falsch übernommen wurde.
 *
 * Jetzt: Bei genau einem bekannten Satz wird er stumm übernommen. Sonst gibt es eine Warnung, die
 * benennt, was in der Datei stand -- der Erfasser entscheidet.
 */
function resolveTaxRate(saetze: readonly GelesenerSteuersatz[]): {
  readonly rate: IncomingTaxRate;
  readonly warnings: readonly string[];
} {
  const warnings: string[] = [];
  if (saetze.length === 0) {
    return { rate: 19, warnings: ['Die Datei enthält keinen erkennbaren Steuersatz. Bitte prüfen und von Hand setzen.'] };
  }

  // Mehrere Positionen können denselben Satz tragen -- je Satz die Bemessungsgrundlage summieren.
  const jeSatz = new Map<number, number>();
  for (const eintrag of saetze) {
    jeSatz.set(eintrag.percent, (jeSatz.get(eintrag.percent) ?? 0) + eintrag.base);
  }
  const eindeutig = [...jeSatz.entries()].map(([percent, base]) => ({ percent, base }));
  // Maßgeblich ist der Satz mit der größten Bemessungsgrundlage -- nicht einfach der erste im XML.
  eindeutig.sort((a, b) => b.base - a.base || b.percent - a.percent);
  const fuehrend = eindeutig[0];

  if (eindeutig.length > 1) {
    warnings.push(
      `Die Rechnung enthält mehrere Steuersätze (${eindeutig.map((eintrag) => formatPercent(eintrag.percent)).join(', ')}). `
      + `FORQ erfasst eine Eingangsrechnung mit EINEM Satz -- übernommen wurde ${formatPercent(fuehrend.percent)}. `
      + 'Bitte die Rechnung aufteilen oder von Hand erfassen.',
    );
  }

  if (isAllowedRate(fuehrend.percent)) {
    return { rate: fuehrend.percent, warnings };
  }

  // Unbekannter Satz: NICHT stillschweigend runden. Der nächstgelegene wird nur vorgeschlagen.
  let vorschlag: IncomingTaxRate = 19;
  let bestDiff = Infinity;
  for (const rate of ALLOWED_RATES) {
    const diff = Math.abs(rate - fuehrend.percent);
    if (diff < bestDiff) { bestDiff = diff; vorschlag = rate; }
  }
  warnings.push(
    `Der Steuersatz ${formatPercent(fuehrend.percent)} aus der Datei wird von FORQ nicht geführt `
    + `(zulässig sind 0 %, 7 % und 19 %). Vorgeschlagen ist ${formatPercent(vorschlag)} -- bitte prüfen.`,
  );
  return { rate: vorschlag, warnings };
}

/** Alle Elemente mit diesem lokalen Namen (Präfix egal). */
function allByLocal(root: Element, localName: string): readonly Element[] {
  return Array.from(root.getElementsByTagNameNS('*', localName));
}

function childByLocal(parent: Element, localName: string): Element | null {
  for (const child of Array.from(parent.children)) {
    if (child.localName === localName) return child;
  }
  return null;
}

/** Erster Nachfahre (beliebige Tiefe) mit dem lokalen Namen, Präfix egal. */
function descByLocal(root: Element, localName: string): Element | null {
  const found = root.getElementsByTagNameNS('*', localName);
  return found.length > 0 ? found[0] : null;
}

/** Pfad lokaler Namen ab `root` entlanglaufen (je Schritt der erste passende Nachfahre) -> Text. */
function pathText(root: Element, ...localNames: string[]): string {
  let current: Element | null = root;
  for (const name of localNames) {
    if (!current) return '';
    current = descByLocal(current, name);
  }
  return current ? (current.textContent ?? '').trim() : '';
}

function toNumber(text: string): number {
  const value = Number.parseFloat(text.replace(',', '.'));
  return Number.isFinite(value) ? value : 0;
}

/** CII-Datum ist YYYYMMDD (Format 102); UBL liefert bereits YYYY-MM-DD. */
function normalizeDate(raw: string): string {
  const value = raw.trim();
  if (/^\d{8}$/.test(value)) return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
  return value.slice(0, 10);
}

function parseUbl(root: Element): ParsedEInvoice {
  const supplierParty = descByLocal(root, 'AccountingSupplierParty');
  const supplierName = supplierParty
    ? (pathText(supplierParty, 'PartyLegalEntity', 'RegistrationName') || pathText(supplierParty, 'PartyName', 'Name'))
    : '';
  const supplierVatId = supplierParty ? pathText(supplierParty, 'PartyTaxScheme', 'CompanyID') : '';
  // ALLE TaxSubtotal lesen, nicht nur das erste -- eine Rechnung darf mehrere Steuersätze führen.
  const saetze = allByLocal(root, 'TaxSubtotal').map((subtotal) => ({
    percent: toNumber(pathText(subtotal, 'Percent')),
    base: toNumber(pathText(subtotal, 'TaxableAmount')),
  }));
  const steuer = resolveTaxRate(saetze);
  return {
    format: 'ubl',
    supplierName,
    supplierVatId,
    supplierInvoiceNumber: (childByLocal(root, 'ID')?.textContent ?? '').trim(),
    invoiceDate: normalizeDate((childByLocal(root, 'IssueDate')?.textContent ?? '').trim()),
    netAmount: toNumber(pathText(root, 'LegalMonetaryTotal', 'TaxExclusiveAmount')),
    taxRate: steuer.rate,
    warnings: steuer.warnings,
  };
}

function parseCii(root: Element): ParsedEInvoice {
  const exchangedDoc = descByLocal(root, 'ExchangedDocument');
  const seller = descByLocal(root, 'SellerTradeParty');
  const summation = descByLocal(root, 'SpecifiedTradeSettlementHeaderMonetarySummation');
  // ALLE ApplicableTradeTax lesen -- in CII steht je Steuersatz ein eigener Block.
  const saetze = allByLocal(root, 'ApplicableTradeTax').map((tradeTax) => ({
    percent: toNumber(pathText(tradeTax, 'RateApplicablePercent')),
    base: toNumber(pathText(tradeTax, 'BasisAmount')),
  }));
  const steuer = resolveTaxRate(saetze);
  return {
    format: 'cii',
    supplierName: seller ? (childByLocal(seller, 'Name')?.textContent ?? '').trim() : '',
    supplierVatId: seller ? pathText(seller, 'SpecifiedTaxRegistration', 'ID') : '',
    supplierInvoiceNumber: exchangedDoc ? (childByLocal(exchangedDoc, 'ID')?.textContent ?? '').trim() : '',
    invoiceDate: normalizeDate(exchangedDoc ? pathText(exchangedDoc, 'IssueDateTime', 'DateTimeString') : ''),
    netAmount: summation ? toNumber(pathText(summation, 'TaxBasisTotalAmount')) : 0,
    taxRate: steuer.rate,
    warnings: steuer.warnings,
  };
}

/** Parst XRechnung- (UBL) oder ZUGFeRD-XML (CII) in die Erfassungsfelder, oder erklärt, warum nicht. */
export function parseEInvoiceXml(xml: string): EInvoiceParseResult {
  if (typeof DOMParser === 'undefined') {
    return { ok: false, error: 'XML kann in dieser Umgebung nicht gelesen werden.' };
  }
  const dom = new DOMParser().parseFromString(xml, 'application/xml');
  if (dom.getElementsByTagName('parsererror').length > 0 || !dom.documentElement) {
    return { ok: false, error: 'Die Datei ist keine gültige XML.' };
  }
  const root = dom.documentElement;
  const data = root.localName === 'CrossIndustryInvoice'
    ? parseCii(root)
    : (root.localName === 'Invoice' || root.localName === 'CreditNote')
      ? parseUbl(root)
      : null;
  if (!data) {
    return { ok: false, error: 'Kein bekanntes E-Rechnungs-Format (XRechnung/UBL oder ZUGFeRD/CII) erkannt.' };
  }
  if (!data.supplierName && !data.supplierInvoiceNumber && data.netAmount === 0) {
    return { ok: false, error: 'Die E-Rechnung enthält keine erkennbaren Rechnungsdaten.' };
  }
  return { ok: true, data };
}
